import type { MouseEvent, ReactElement } from "react";
import { Typography, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { Datagrid, FieldProps, FunctionField, TextField, useListContext, useRecordContext } from "react-admin";
import { useNavigate } from "react-router";
import { DateField } from "../../fields/DateField";
import { renderAvatarAndName } from "../../lists/StudentsDataGrid";
import { Column } from "../../misc/Column";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import type { Student } from "../../providers/studentsProvider";
import { ExaminationAssignmentMobileListItem } from "./ExaminationAssignmentMobileListItem";
import { getExamPlannedDisplayValue } from "./examinationAssignmentUtils";

export function ExaminationAssignmentsDataGrid(props: { empty?: ReactElement }) {
  const { empty } = props;
  const { isLoading, data, total } = useListContext<Student>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  if (isLoading) {
    return <LoadingIndicator />;
  }

  const handleMobileItemClick = (event: MouseEvent<HTMLDivElement>, studentId: string) => {
    if (event.ctrlKey || event.metaKey) {
      window.open(`/students/${studentId}`, "_blank");
      return;
    }
    navigate(`/students/${studentId}`);
  };

  if (isMobile) {
    if (!data || data.length === 0 || total === 0) {
      return empty ?? null;
    }

    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {data.map((student) => (
          <ExaminationAssignmentMobileListItem
            key={student.id}
            student={student}
            onClick={(event) => handleMobileItemClick(event, student.id)}
          />
        ))}
      </Column>
    );
  }

  return (
    <Datagrid rowClick={(id) => `/students/${id}`} bulkActionButtons={false} empty={empty}>
      <FunctionField label="Fahrschüler" source="name" render={renderAvatarAndName} />
      <TextField label="Status des Fahrschülers" source="verboseStatus" />
      <DateField label="Anmeldung" source="startDate" />
      <DateField label="Eingangsdatum" source="examinationAssignmentReceivedAt" />
      <DateField label="Ablaufdatum" source="examinationAssignmentExpiresAt" showDate="with diff" />
      <ExamPlannedField label="Prüfungstermin organisiert?" />
    </Datagrid>
  );
}

function ExamPlannedField(props: FieldProps<Student>) {
  const student = useRecordContext<Student>(props);
  return (
    <Typography component="span" variant="body2">
      {getExamPlannedDisplayValue(student)}
    </Typography>
  );
}
